import {
    createCacheKeyBuilder,
    updateEntityCache,
    updateEntityListCache,
} from './createPaginationEntityCacheHelpers';
import type {
    CustomQueryPageParams,
    QueryPage,
    QueryPageReturnType,
    QueryParamsBaseType,
} from './createPaginationEntityTypes';
import type { CacheKey } from '../utils/internalTypings';

export function getNextPageParam<QueryParams extends QueryParamsBaseType, Entity>(
    page?: QueryPageReturnType<Entity>,
): CustomQueryPageParams<QueryParams> | undefined {
    const currentPage = page?.pageNumber || 0;
    const pageCount = page?.pageCount || 0;
    const nextPage = currentPage + 1;
    if (nextPage < pageCount) {
        return {
            pageNumber: nextPage,
        } as CustomQueryPageParams<QueryParams>;
    }

    return undefined;
}

export function getInfiniteQueryFn<
    QueryParams extends QueryParamsBaseType,
    Entity,
    AdditionalData,
>({
    queryPage,
    params,
    name,
    selectId,
    skipParamsHashing,
    cancelable = false,
    skipEntityCacheSync,
}: {
    queryPage: QueryPage<Entity, QueryParams, AdditionalData>;
    params: QueryParams | CustomQueryPageParams<QueryParams>;
    name: string;
    cancelable?: boolean;
    skipParamsHashing?: boolean;
    skipEntityCacheSync: boolean;
    selectId: CacheKey<Entity>;
}) {
    const fn = async ({
        pageParam,
        signal,
    }: {
        pageParam?: Partial<QueryParams>;
        signal?: AbortSignal;
    }): Promise<QueryPageReturnType<Entity, AdditionalData>> => {
        const res = await queryPage({ pageNumber: 0, ...params, ...pageParam }, { signal });
        if (skipEntityCacheSync) {
            return res;
        }

        // extract array of entities from response
        const entities = res.data;

        // save each entity to "detail" state
        entities.forEach((entity) => {
            updateEntityCache(entity, selectId(entity), { name });
        });

        // update entity in all already fetched pages
        updateEntityListCache(entities, {
            name,
            selectId,
            // no need to persist current key - will be done automatically by query client
            exclude: createCacheKeyBuilder(name).list(skipParamsHashing ? {} : params),
        });

        return res;
    };

    if (cancelable) {
        return ({
            pageParam,
            signal,
        }: {
            pageParam?: CustomQueryPageParams<QueryParams>;
            signal?: AbortSignal;
        }) => fn({ pageParam, signal });
    }

    // if we don't want react-query to cancel our request, we must not consume (have a getter) for the signal property
    return ({
        pageParam,
    }: {
        pageParam?: CustomQueryPageParams<QueryParams>;
        signal?: AbortSignal;
    }) => fn({ pageParam });
}

export function getQueryFn<Entity, QueryByIdParams extends QueryParamsBaseType>({
    queryById,
    id,
    params,
    name,
    selectId,
    localListsSynchronisation,
    cancelable = false,
    skipEntityCacheSync,
}: {
    params?: QueryByIdParams;
    queryById: (id: string, params?: QueryByIdParams, signal?: AbortSignal) => Promise<Entity>;
    id: string;
    name: string;
    cancelable?: boolean;
    localListsSynchronisation: boolean | undefined;
    selectId: CacheKey<Entity>;
    skipEntityCacheSync: boolean;
}) {
    const fn = async ({ signal }: { signal?: AbortSignal } = {}): Promise<Entity | undefined> => {
        const response = await queryById(id, params, signal);

        if (skipEntityCacheSync) {
            return response;
        }

        if (!response) {
            return undefined;
        }

        updateEntityListCache([response], {
            name,
            selectId,
            operation: localListsSynchronisation ? 'APPEND' : undefined,
        });

        return response;
    };

    if (cancelable) {
        return ({ signal }: { signal?: AbortSignal }) => fn({ signal });
    }

    // if we don't want react-query to cancel our request, we must not consume (have a getter) for the signal property
    return () => fn();
}
